import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import '../styles/Instructions.css';

function Instructions() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
      } else {
        setUser(user);
      }
    };
    checkAuth();
  }, [navigate]);

  if (!user) return null;

  return (
    <div className="instructions-container">
      <div className="instructions-header">
        <h1 className="instructions-title">
          <span className="icon">🧪</span> FFA Test Guide
        </h1>
        <p className="instructions-subtitle">Quick reference for Free Fatty Acid testing</p>
      </div>

      {/* Glassware & Equipment Needed */}
      <div className="equipment-container">
        <h2 className="equipment-title">
          <span className="icon">🧪</span> Glassware & Equipment Needed
        </h2>
        <div className="equipment-grid">
          <div className="equipment-item">
            <span className="equipment-icon">🧪</span>
            <div>
              <strong>100ml Conical Flask</strong>
              <div className="equipment-desc">For oil sample mixing</div>
            </div>
          </div>
          <div className="equipment-item">
            <span className="equipment-icon">📏</span>
            <div>
              <strong>25ml Measuring Cylinder</strong>
              <div className="equipment-desc">For measuring ethanol</div>
            </div>
          </div>
          <div className="equipment-item">
            <span className="equipment-icon">⚖️</span>
            <div>
              <strong>Analytical Scale</strong>
              <div className="equipment-desc">For precise oil weighing</div>
            </div>
          </div>
          <div className="equipment-item">
            <span className="equipment-icon">🔥</span>
            <div>
              <strong>Hot Plate</strong>
              <div className="equipment-desc">Temperature controlled to 60°C</div>
            </div>
          </div>
          <div className="equipment-item">
            <span className="equipment-icon">💧</span>
            <div>
              <strong>25ml Burette with Stand</strong>
              <div className="equipment-desc">For titration</div>
            </div>
          </div>
          <div className="equipment-item">
            <span className="equipment-icon">🔳</span>
            <div>
              <strong>Mesh Filter</strong>
              <div className="equipment-desc">For expeller oil extraction</div>
            </div>
          </div>
        </div>
      </div>

      <div className="zones-container">
        {/* Zone 1: Preparation */}
        <div className="zone-card zone-prep">
          <div className="zone-header">
            <span className="zone-icon">✅</span>
            <h2>Zone 1: Preparation</h2>
          </div>
          <div className="zone-content">
            <div className="step-group">
              <h3>Reagents</h3>
              <div className="step">
                <span className="step-number">1</span>
                <div>
                  <strong>NaOH Solution:</strong> Use pre-made 0.1N bottle or prepare fresh
                </div>
              </div>
              <div className="step">
                <span className="step-number">2</span>
                <div>
                  <strong>Ethanol:</strong> Keep 99.9% ethanol ready
                </div>
              </div>
            </div>
            <div className="step-group">
              <h3>Seeds</h3>
              <div className="step">
                <span className="step-number">3</span>
                <div>
                  Take sesame seeds → Dry at 60°C if needed → Cool → Check moisture %
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Zone 2: Oil Extraction */}
        <div className="zone-card zone-extract">
          <div className="zone-header">
            <span className="zone-icon">🔥</span>
            <h2>Zone 2: Oil Extraction</h2>
          </div>
          <div className="zone-content">
            <div className="step">
              <span className="step-number">4</span>
              <div>
                <strong>Heat expeller:</strong> 180°C - 200°C
              </div>
            </div>
            <div className="step">
              <span className="step-number">5</span>
              <div>
                <strong>Add seeds:</strong> Pour into hopper
              </div>
            </div>
            <div className="step warning-step">
              <span className="step-number">6</span>
              <div>
                <strong>Discard first 5 seconds</strong> of oil (contamination)
              </div>
            </div>
            <div className="step">
              <span className="step-number">7</span>
              <div>
                <strong>Collect oil:</strong> Use clean beaker with mesh filter
              </div>
            </div>
          </div>
        </div>

        {/* Zone 3: Sample Measurement */}
        <div className="zone-card zone-measure">
          <div className="zone-header">
            <span className="zone-icon">⚖️</span>
            <h2>Zone 3: Sample Measurement</h2>
          </div>
          <div className="zone-content">
            <div className="step">
              <span className="step-number">8</span>
              <div>
                <strong>Weigh oil:</strong> ~2.5g in conical flask (note exact weight)
              </div>
            </div>
            <div className="step">
              <span className="step-number">9</span>
              <div>
                <strong>Add alcohol:</strong> 20 mL ethanol → Shake well
              </div>
            </div>
          </div>
        </div>

        {/* Zone 4: Heating & Mixing */}
        <div className="zone-card zone-heat">
          <div className="zone-header">
            <span className="zone-icon">♨️</span>
            <h2>Zone 4: Heating & Mixing</h2>
          </div>
          <div className="zone-content">
            <div className="step">
              <span className="step-number">10</span>
              <div>
                <strong>Heat at 60°C:</strong> Place conical flask on hot plate until bubbles appear
              </div>
            </div>
            <div className="step">
              <span className="step-number">11</span>
              <div>
                <strong>Shake well</strong> when bubbling → Repeat heating & shaking
              </div>
            </div>
          </div>
        </div>

        {/* Zone 5: Titration */}
        <div className="zone-card zone-titration">
          <div className="zone-header">
            <span className="zone-icon">🧪</span>
            <h2>Zone 5: Titration</h2>
          </div>
          <div className="zone-content">
            <div className="step">
              <span className="step-number">12</span>
              <div>
                <strong>Add indicator:</strong> 2 drops phenolphthalein
              </div>
            </div>
            <div className="step">
              <span className="step-number">13</span>
              <div>
                <strong>Setup burette:</strong> Fill with 0.1N NaOH, note initial reading
              </div>
            </div>
            <div className="step">
              <span className="step-number">14</span>
              <div>
                <strong>Titrate slowly:</strong> Swirl continuously
              </div>
            </div>
            <div className="step success-step">
              <span className="step-number">15</span>
              <div>
                <strong>Stop at faint pink</strong> that stays for 30 seconds
              </div>
            </div>
          </div>
        </div>

        {/* Zone 6: Calculation */}
        <div className="zone-card zone-calc">
          <div className="zone-header">
            <span className="zone-icon">📐</span>
            <h2>Zone 6: Calculation</h2>
          </div>
          <div className="zone-content">
            <div className="formula-box">
              <div className="formula-title">FFA % Formula</div>
              <div className="formula">
                FFA % = (V × N × 28.2) / W
              </div>
              <div className="formula-legend">
                <div><strong>V</strong> = Volume of NaOH used (mL)</div>
                <div><strong>N</strong> = Normality of NaOH (0.1)</div>
                <div><strong>W</strong> = Weight of oil sample (g)</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Reference Timeline */}
      <div className="timeline-container">
        <h2 className="timeline-title">Quick Process Flow</h2>
        <div className="timeline">
          <div className="timeline-item">
            <div className="timeline-icon">✅</div>
            <div className="timeline-text">Prep</div>
          </div>
          <div className="timeline-arrow">→</div>
          <div className="timeline-item">
            <div className="timeline-icon">🔥</div>
            <div className="timeline-text">Extract</div>
          </div>
          <div className="timeline-arrow">→</div>
          <div className="timeline-item">
            <div className="timeline-icon">⚖️</div>
            <div className="timeline-text">Measure</div>
          </div>
          <div className="timeline-arrow">→</div>
          <div className="timeline-item">
            <div className="timeline-icon">♨️</div>
            <div className="timeline-text">Heat</div>
          </div>
          <div className="timeline-arrow">→</div>
          <div className="timeline-item">
            <div className="timeline-icon">🧪</div>
            <div className="timeline-text">Titrate</div>
          </div>
          <div className="timeline-arrow">→</div>
          <div className="timeline-item">
            <div className="timeline-icon">📐</div>
            <div className="timeline-text">Calculate</div>
          </div>
        </div>
      </div>

      {/* Key Points */}
      <div className="key-points">
        <h3>🔑 Key Points to Remember</h3>
        <div className="points-grid">
          <div className="point">
            <span className="point-icon">⚠️</span>
            Always discard first 5 seconds of oil
          </div>
          <div className="point">
            <span className="point-icon">🌡️</span>
            Maintain 60°C for heating steps
          </div>
          <div className="point">
            <span className="point-icon">🎯</span>
            Use white paper or tile background to check for pale pink color
          </div>
          <div className="point">
            <span className="point-icon">📏</span>
            Record exact weight and volumes
          </div>
        </div>
      </div>

      {/* Cleanup Section */}
      <div className="cleanup-section">
        <h2 className="cleanup-title">
          <span className="icon">🧹</span> Cleanup Procedure
        </h2>
        <div className="cleanup-content">
          <div className="cleanup-steps">
            <div className="cleanup-step">
              <span className="cleanup-number">1</span>
              <div>
                <strong>Wash all glassware</strong> with dish soap liquid
              </div>
            </div>
            <div className="cleanup-step">
              <span className="cleanup-number">2</span>
              <div>
                <strong>Use gentle brush</strong> to remove oily and chemical residue
              </div>
            </div>
            <div className="cleanup-step">
              <span className="cleanup-number">3</span>
              <div>
                <strong>Rinse thoroughly</strong> with distilled water
              </div>
            </div>
            <div className="cleanup-step">
              <span className="cleanup-number">4</span>
              <div>
                <strong>Dry in hot oven</strong> at 105°C for 15 min or as needed
              </div>
            </div>
          </div>
          <div className="cleanup-note">
            <span className="note-icon">⚠️</span>
            Proper cleanup ensures accurate results in future tests
          </div>
        </div>
      </div>
    </div>
  );
}

export default Instructions;